import {
  CompanyCardSchema,
  isDefined,
  isEmpty,
  isNotEmpty,
  type CompanyCard,
  type CompanyCardSections,
} from '@terros/common'

type ExistingCardRouting = Pick<CompanyCard, 'sections' | 'teamInboxEmail' | 'starThreshold'>

type ReviewRoutingInput = {
  sections?: Pick<CompanyCardSections, 'cta'>
  placeId?: string
  feedbackInboxEnabled: boolean
  teamInboxEmail?: string
  starThreshold?: number
}

type InitialReviewRoutingValues = Pick<ReviewRoutingInput, 'feedbackInboxEnabled' | 'teamInboxEmail' | 'starThreshold'>

type ReviewRoutingState = {
  showReviewSettings: boolean
  showFeedbackInbox: boolean
  showFeedbackFields: boolean
  profileIsValid: boolean
  emailIsValid: boolean
  thresholdIsValid: boolean
}

export function getInitialReviewRoutingValues(
  existingCard: ExistingCardRouting | undefined
): InitialReviewRoutingValues {
  const savedValues = {
    teamInboxEmail: existingCard?.teamInboxEmail,
    starThreshold: existingCard?.starThreshold ?? 4,
  }
  if (existingCard?.sections?.feedbackInbox === false) {
    return { ...savedValues, feedbackInboxEnabled: false }
  }
  return {
    ...savedValues,
    feedbackInboxEnabled: true,
  }
}

function getSavedReviewSections({
  sections,
  feedbackInboxEnabled,
  showSocials,
}: {
  sections: CompanyCardSections | undefined
  feedbackInboxEnabled: boolean
  showSocials: boolean
}): CompanyCardSections | undefined {
  if (!isDefined(sections)) return undefined
  return { ...sections, socials: showSocials, feedbackInbox: feedbackInboxEnabled }
}

export function getSavedReviewRoutingValues({
  values,
  savedThreshold,
  showSocials,
}: {
  values: ReviewRoutingInput & { sections?: CompanyCardSections }
  savedThreshold: number | undefined
  showSocials: boolean
}): Pick<CompanyCard, 'sections' | 'teamInboxEmail' | 'starThreshold'> {
  return {
    sections: getSavedReviewSections({
      sections: values.sections,
      feedbackInboxEnabled: values.feedbackInboxEnabled,
      showSocials,
    }),
    teamInboxEmail: values.teamInboxEmail,
    starThreshold: getSavedReviewThreshold(values, savedThreshold),
  }
}

export function getSavableTeamInboxEmail(
  values: ReviewRoutingInput,
  savedEmail: string | undefined
): string | undefined {
  if (getReviewRoutingState(values).showFeedbackFields) return values.teamInboxEmail
  if (
    isNotEmpty(values.teamInboxEmail) &&
    CompanyCardSchema.shape.teamInboxEmail.safeParse(values.teamInboxEmail).success
  ) {
    return values.teamInboxEmail
  }
  return savedEmail
}

export function getReviewRoutingState({
  sections,
  placeId,
  feedbackInboxEnabled,
  teamInboxEmail,
  starThreshold,
}: ReviewRoutingInput): ReviewRoutingState {
  const showReviewSettings = sections?.cta ?? true
  if (!showReviewSettings) {
    return {
      showReviewSettings: false,
      showFeedbackInbox: false,
      showFeedbackFields: false,
      profileIsValid: true,
      emailIsValid: true,
      thresholdIsValid: true,
    }
  }
  if (isEmpty(placeId)) {
    return {
      showReviewSettings: true,
      showFeedbackInbox: false,
      showFeedbackFields: false,
      profileIsValid: false,
      emailIsValid: true,
      thresholdIsValid: true,
    }
  }
  if (!feedbackInboxEnabled) {
    return {
      showReviewSettings: true,
      showFeedbackInbox: true,
      showFeedbackFields: false,
      profileIsValid: true,
      emailIsValid: true,
      thresholdIsValid: true,
    }
  }

  return {
    showReviewSettings: true,
    showFeedbackInbox: true,
    showFeedbackFields: true,
    profileIsValid: true,
    emailIsValid:
      isNotEmpty(teamInboxEmail) && CompanyCardSchema.shape.teamInboxEmail.safeParse(teamInboxEmail).success,
    thresholdIsValid:
      isDefined(starThreshold) && CompanyCardSchema.shape.starThreshold.safeParse(starThreshold).success,
  }
}

export function getReviewRoutingErrors(
  values: ReviewRoutingInput,
  translate: (message: string) => string
): Record<string, string> {
  const state = getReviewRoutingState(values)
  const errors: Record<string, string> = {}
  if (!state.profileIsValid) errors.placeId = translate('Select your Google Business Profile')
  if (!state.emailIsValid) errors.teamInboxEmail = translate('Enter a valid feedback inbox email')
  if (!state.thresholdIsValid) errors.starThreshold = translate('Select a positive review threshold')
  return errors
}

export function getSavedReviewThreshold(
  values: ReviewRoutingInput,
  savedThreshold: number | undefined
): number | undefined {
  if (!getReviewRoutingState(values).showFeedbackFields) return savedThreshold
  return values.starThreshold
}
